/**
 * Return a fully formatted currency string
 *
 * @param currency - ISO currency symbol (e.g. USD)
 * @param amount - Amount (in cents)
 * @param locale - Fallback locale
 * @example - $550,000
 */
export function formatted(currency: string, amount: number, locale = "en-US") {
  const up = amount / 100;
  try {
    return up.toLocaleString(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: up % 1 === 0 ? 0 : 2,
    });
  } catch {
    console.error(`Failed to format currency ${currency} ${amount} with locale ${locale}`);
    return up.toLocaleString(locale);
  }
}

/**
 * Format an invoice amount (in cents). Tries currency formatting first; falls back to
 * plain decimal with 2 fraction digits if Intl.NumberFormat throws (e.g. invalid or
 * empty currency code).
 *
 * @param totalInCents - Amount in cents
 * @param currency - ISO currency code (string)
 * @param locale - Locale for formatting (default en-US)
 */
export function formatInvoiceAmount(totalInCents: number, currency: string, locale = "en-US"): string {
  const amount = totalInCents / 100;
  const decimalOptions = {
    style: "decimal" as const,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
  } catch {
    console.error(`Failed to format currency ${currency} ${amount} with locale ${locale}`);
    return new Intl.NumberFormat(locale, decimalOptions).format(amount);
  }
}

/**
 * Remove the currency code (in brackets only) from a string
 *
 * @param str - String to remove the code from
 * @description - If the string contains a currency code in brackets, it will be removed if its not
 * then remove last 3 characters from the string that has a space before it
 */
export function stripCode(str: string): string {
  const withBracketsRegex = new RegExp(/\([A-Z]+\)/);

  if (withBracketsRegex.test(str)) {
    return str.replace(/\([A-Z]+\)/g, "").trim();
  }

  return str.replace(/\s[A-Za-z]{3}$/g, "").trim();
}
